// ============================================================================
// FSA NARRATIVE COMPONENT VIEW MODAL (ADR-0005)
// ============================================================================
// Read-only viewer for one installed narrative component version, opened
// from the Template Browser's NARRATIVE rows. Fetches the full body on
// open (list rows omit bodies) and renders it like the template browser's
// NarrativePreview — formatted body + placeholder palette.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { fsaNarrativeService } from 'api/services/fsa/fsaNarrativeService';
import type { NarrativeComponent } from 'api/types/fsa/narrative.types';
import type { NarrativePlaceholder } from 'api/types/fsa/template.types';

interface Props {
  isOpen: boolean;
  /** Component code to show. */
  narrativeCode: string | null;
  /** Exact version; null/undefined = latest installed. */
  version?: number | null;
  onClose: () => void;
}

const FsaNarrativeViewModal: React.FC<Props> = ({ isOpen, narrativeCode, version, onClose }) => {
  const [component, setComponent] = useState<NarrativeComponent | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !narrativeCode) { setComponent(null); return; }
    let cancelled = false;
    setLoading(true);
    fsaNarrativeService.getComponent(narrativeCode, version)
      .then((res) => { if (!cancelled) setComponent(res.data ?? null); })
      .catch((e: any) => {
        if (!cancelled) {
          notificationService.toast.error(e?.response?.data?.message || e?.message || 'Failed to load narrative component.');
          onClose();
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, narrativeCode, version]);

  const placeholders: NarrativePlaceholder[] = useMemo(() => {
    try { return JSON.parse(component?.placeholdersJson || '[]'); } catch { return []; }
  }, [component?.placeholdersJson]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="modal-title" style={{ margin: 0 }}>
              {component?.name || narrativeCode || 'Narrative Component'}
            </h2>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
              {narrativeCode}{component ? ` · v${component.version}` : ''}
              {component?.publishedAt ? ` · installed ${new Date(component.publishedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto' }}>
          {loading && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
          )}

          {!loading && component && (
            <>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                <IconSystem.info size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Immutable component version — engagements read the version they are pinned to;
                this viewer always shows the requested/latest installed version.
              </div>

              <div style={paperStyle}>
                {component.bodyHtml
                  ? <div style={bodyStyle} dangerouslySetInnerHTML={{ __html: component.bodyHtml }} />
                  : <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No body authored.</div>}
              </div>

              {placeholders.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                    PLACEHOLDERS ({placeholders.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                    {placeholders.map((ph) => (
                      <span key={ph.token} title={ph.label}
                        style={{ fontSize: '0.7rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 4, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                        {'{{'}{ph.token}{'}}'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

const paperStyle: React.CSSProperties = {
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  padding: '1.25rem 1.5rem',
};
const bodyStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  lineHeight: 1.65,
  color: 'var(--text-primary)',
};

export default FsaNarrativeViewModal;
